import { useEffect, useState } from 'react'
import { Spin } from 'antd'
import type { PortfolioDetail, PortfolioNavPoint } from '@/types/portfolio'
import { getPortfolioNav } from '@/api/portfolio'
import NavChart from '@/components/NavChart'
import type { NavPoint } from '@/types'

function fmtWan(v: number | null | undefined) {
    if (v == null) return '--'
    if (Math.abs(v) >= 10000) return (v / 10000).toFixed(2) + '亿'
    return v.toFixed(2) + '万'
}

function fmtSignedWan(v: number | null | undefined) {
    if (v == null) return '--'
    if (v === 0) return fmtWan(0)
    return `${v > 0 ? '+' : '-'}${fmtWan(Math.abs(v))}`
}

const positionSizingLabel: Record<string, string> = {
    equal_weight: '等权分配',
    market_cap:   '市值加权',
    risk_parity:  '风险平价',
}

function toNavPoint(p: PortfolioNavPoint): NavPoint {
    return {
        trade_date: p.nav_date,
        nav_value: p.nav,
        cash: p.cash,
        cumulative_return_pct: p.cumulative_return_pct,
        daily_return_pct: p.daily_return_pct,
        total_assets: p.total_assets,
        drawdown_pct: 0,
    }
}

export default function Overview({ detail }: { detail: PortfolioDetail }) {
    const [navData, setNavData] = useState<NavPoint[]>([])
    const [navLoading, setNavLoading] = useState(true)

    useEffect(() => {
        setNavLoading(true)
        getPortfolioNav(detail.id, 365)
            .then(res => setNavData((res.items ?? []).map(toNavPoint)))
            .catch(() => {})
            .finally(() => setNavLoading(false))
    }, [detail.id])

    const ret    = detail.cumulative_return_pct ?? 0
    const ann    = detail.annual_return_pct ?? 0
    const dd     = detail.max_drawdown_pct ?? 0
    const wr     = detail.win_rate_pct ?? 0
    const profit = detail.profit_wan ?? 0
    const todayPnl = detail.today_pnl_wan ?? 0
    const todayRet = detail.daily_return_pct ?? 0
    const rp     = (detail.risk_params ?? {}) as Record<string, string | number | null>
    const isLive = detail.type === 'live'

    return (
        <div className="asset-detail__overview">

            {/* 净值走势图 */}
            <div className="asset-detail__nav-card">
                <div className="asset-detail__nav-header">
                    <span className="asset-detail__block-title">净值走势</span>
                    <span className="asset-detail__nav-badge">
                        {isLive ? '实盘' : '模拟盘'}
                    </span>
                </div>
                <div className="asset-detail__nav-chart">
                    {navLoading ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <Spin size="small" />
                        </div>
                    ) : (
                        <NavChart data={navData} valueKey="nav_value" />
                    )}
                </div>
            </div>

            {/* KPI 主指标 */}
            <div className="asset-detail__kpi-row">
                <div className="asset-detail__kpi">
                    <div className="asset-detail__kpi-label">当前总资产</div>
                    <div className="asset-detail__kpi-val">{fmtWan(detail.current_nav_wan)}</div>
                    <div className="asset-detail__kpi-sub">初始资金 {fmtWan(detail.initial_nav_wan)}</div>
                </div>
                <div className="asset-detail__kpi">
                    <div className="asset-detail__kpi-label">累计收益率</div>
                    <div className={`asset-detail__kpi-val ${ret >= 0 ? 'pos' : 'neg'}`}>
                        {ret >= 0 ? '+' : ''}{ret.toFixed(2)}%
                    </div>
                    <div className="asset-detail__kpi-sub">年化 {ann >= 0 ? '+' : ''}{ann.toFixed(1)}%</div>
                </div>
                <div className="asset-detail__kpi">
                    <div className="asset-detail__kpi-label">最大回撤</div>
                    <div className={`asset-detail__kpi-val ${dd > 10 ? 'neg' : ''}`}>
                        -{dd.toFixed(1)}%
                    </div>
                    <div className="asset-detail__kpi-sub">历史胜率 {wr.toFixed(1)}%</div>
                </div>
                <div className="asset-detail__kpi">
                    <div className="asset-detail__kpi-label">持仓天数</div>
                    <div className="asset-detail__kpi-val">{detail.days ?? 0}</div>
                    <div className="asset-detail__kpi-sub">
                        创建 {detail.created_at ? detail.created_at.slice(0, 10) : '--'}
                    </div>
                </div>
            </div>

            {/* KPI 次指标 */}
            <div className="asset-detail__kpi-row asset-detail__kpi-row--2">
                <div className="asset-detail__kpi asset-detail__kpi--sm">
                    <div className="asset-detail__kpi-label">今日盈亏</div>
                    <div className={`asset-detail__kpi-val--sm ${todayPnl >= 0 ? 'pos' : 'neg'}`}>
                        {fmtSignedWan(todayPnl)}
                    </div>
                    <div className="asset-detail__kpi-sub">
                        {todayRet >= 0 ? '+' : ''}{todayRet.toFixed(2)}%
                    </div>
                </div>
                <div className="asset-detail__kpi asset-detail__kpi--sm">
                    <div className="asset-detail__kpi-label">累计盈亏</div>
                    <div className={`asset-detail__kpi-val--sm ${profit >= 0 ? 'pos' : 'neg'}`}>
                        {fmtSignedWan(profit)}
                    </div>
                </div>
                <div className="asset-detail__kpi asset-detail__kpi--sm">
                    <div className="asset-detail__kpi-label">可用现金</div>
                    <div className="asset-detail__kpi-val--sm">{fmtWan(detail.current_cash_wan)}</div>
                </div>
                <div className="asset-detail__kpi asset-detail__kpi--sm">
                    <div className="asset-detail__kpi-label">最近调仓</div>
                    <div className="asset-detail__kpi-val--sm">
                        {detail.last_rebalanced_at ? detail.last_rebalanced_at.slice(0, 10) : '--'}
                    </div>
                </div>
                <div className="asset-detail__kpi asset-detail__kpi--sm">
                    <div className="asset-detail__kpi-label">持仓只数</div>
                    <div className="asset-detail__kpi-val--sm">{detail.position_count ?? 0} 只</div>
                </div>
                <div className="asset-detail__kpi asset-detail__kpi--sm">
                    <div className="asset-detail__kpi-label">调仓次数</div>
                    <div className="asset-detail__kpi-val--sm">{detail.rebalance_count ?? 0} 次</div>
                </div>
                <div className="asset-detail__kpi asset-detail__kpi--sm">
                    <div className="asset-detail__kpi-label">夏普比率</div>
                    <div className={`asset-detail__kpi-val--sm ${(detail.sharpe_ratio ?? 0) < 0 ? 'neg' : ''}`}>
                        {detail.sharpe_ratio != null ? detail.sharpe_ratio.toFixed(2) : '--'}
                    </div>
                </div>
            </div>

            {/* 基本信息 */}
            <div className="asset-detail__info-block">
                <div className="asset-detail__block-title">基本信息</div>
                <div className="asset-detail__info-grid">
                    {[
                        { label: '基金经理',   val: detail.manager_name || '--' },
                        { label: '策略版本',   val: detail.strategy_name ? `${detail.strategy_name} ${detail.strategy_version_no ?? ''}`.trim() : '--' },
                        { label: '运营模式',   val: isLive ? '实盘' : '模拟盘' },
                        { label: '建仓开始',   val: detail.started_at ? detail.started_at.slice(0, 10) : '--' },
                        { label: '创建时间',   val: detail.created_at ? detail.created_at.slice(0, 10) : '--' },
                        { label: '最近更新',   val: detail.updated_at ? detail.updated_at.slice(0, 10) : '--' },
                    ].map(({ label, val }) => (
                        <div key={label} className="asset-detail__info-row">
                            <span className="asset-detail__info-label">{label}</span>
                            <span className="asset-detail__info-val">{val}</span>
                        </div>
                    ))}
                </div>
            </div>

            {detail.description && (
                <div className="asset-detail__desc-block">
                    <div className="asset-detail__block-title">组合描述</div>
                    <div className="asset-detail__desc-text">{detail.description}</div>
                </div>
            )}

            {/* 风控配置 */}
            <div className="asset-detail__risk-block">
                <div className="asset-detail__block-title">风控配置</div>
                <div className="asset-detail__risk-rows">
                    {[
                        { label: '单股仓位上限', val: rp.max_single_weight != null ? `${rp.max_single_weight}%` : '--', hint: '每只股最大权重' },
                        { label: '行业仓位上限', val: rp.sector_limit     != null ? `${rp.sector_limit}%`     : '--', hint: '单行业集中度上限' },
                        { label: '最大回撤止损', val: rp.max_drawdown     != null ? `${rp.max_drawdown}%`     : '--', hint: '触发强制平仓线' },
                        { label: '最低现金留存', val: rp.cash_reserve     != null ? `${rp.cash_reserve}%`     : '--', hint: '不可动用底仓' },
                        {
                            label: '仓位分配方式',
                            val: rp.position_sizing
                                ? (positionSizingLabel[String(rp.position_sizing)] ?? String(rp.position_sizing))
                                : '--',
                            hint: `最多 ${rp.max_positions ?? '--'} 只`,
                        },
                        {
                            label: 'AI 决策频率',
                            val: ({
                                daily: '每日',
                                weekly: '每周',
                                monthly: '每月',
                            } as Record<string, string>)[String(rp.rebalance_frequency)] ?? '每日',
                            hint: '当地收盘前 30–45 分钟执行',
                        },
                    ].map(({ label, val, hint }) => (
                        <div key={label} className="asset-detail__risk-row">
                            <span className="asset-detail__risk-label">{label}</span>
                            <span className="asset-detail__risk-val">{val}</span>
                            <span className="asset-detail__risk-hint">{hint}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
